import React from "react";
import { motion } from "framer-motion";
import { Words, FadeUp, PopIn, EASE } from "@/components/Reveal";
import PillButton from "@/components/PillButton";

const CHAPTERS = [
  {
    label: "Background",
    heading: "Colorado roots. A heart for people.",
    text: "Colorado has always been home. It's where Stephanie's faith, family, and love for community have grown over the years. Through ministry, advocacy, and serving alongside others, she's learned that lasting impact usually begins with one honest conversation at a time. Those experiences continue to shape the way she listens, asks questions, and welcomes every guest to the table.",
    image: "/figma/cause-community.png",
    alt: "Friends in warm conversation around a table",
  },
  {
    label: "Testimony",
    heading: "The moment the ordinary changed",
    text: "Stephanie's own testimony is short on drama and long on grace — a slow turning, a book left open, a prayer answered in a way she didn't ask for. It's what she asks her guests about first, because it is the thread that ties every other chapter together.",
    image: "/figma/about-media-overlay.jpg",
    alt: "A warm podcast studio — microphone and headphones ready for conversation",
  },
  {
    label: "Faith",
    heading: "A daily walk, not a title",
    text: "Faith at Stephanie's table looks like showing up early, staying late, and asking the honest question. It's less about the podium and more about the porch — where the Word gets tested against a real week.",
    image: "/figma/cause-education.png",
    alt: "A notebook and pen in warm light",
  },
  {
    label: "Family",
    heading: "Home is the loudest classroom",
    text: "Wife, mother, sister, friend — Stephanie treats family life as the primary text. The rhythms of a shared meal, a bedtime prayer, and a car full of questions are the material every episode is built on.",
    image: "/figma/cause-family.png",
    alt: "Friends arm in arm at golden hour",
  },
  {
    label: "Education",
    heading: "Learning as an act of love",
    text: "Formed by mentors, sharpened by good teachers, and committed to lifelong study — Stephanie sees education as one of the most tangible ways a community loves the next generation well.",
    image: "/figma/ep-3.png",
    alt: "Coffee and journal on a wooden table",
  },
  {
    label: "Mission",
    heading: "Handing the microphone forward",
    text: "Every conversation on the podcast has one goal: make room for someone else to say what God is doing. The mission is simple — hand the microphone forward and let the testimony do the work.",
    image: "/figma/cause-missions.jpg",
    alt: "Two hands reaching toward each other",
  },
];

const VALUES = [
  {
    title: "Faith",
    sub: "Jesus is the center.",
    text: "The center of every conversation. Not a footnote, not a filter — the foundation Stephanie builds from and returns to.",
  },
  {
    title: "Family",
    sub: "The first calling",
    text: "Marriage, motherhood, and the quiet work of raising children who know they are loved and known.",
  },
  {
    title: "Freedom",
    sub: "A country worth stewarding",
    text: "Faithful citizenship close to home — showing up, learning the founding, and taking the next right step.",
  },
  {
    title: "Fellowship",
    sub: "Real conversations change lives.",
    text: "Testimony is oxygen. Stephanie makes room for stories that widen the room and steady the heart.",
  },
];

const About = () => (
  <main className="bg-white">
    {/* Hero */}
    <section className="w-full bg-white pt-[110px] sm:pt-[128px] lg:pt-[150px]">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
        <div className="mx-auto grid max-w-[1208px] grid-cols-1 items-center gap-12 md:grid-cols-2 md:gap-[56px] lg:gap-[72px] lg:px-[60px]">
          <div className="flex flex-col gap-6 lg:gap-8">
            <Words text="About Stephanie" mode="load" delay={0.1} stagger={0.08} className="eyebrow inline-flex overflow-hidden text-[13px] leading-[22.4px] text-black sm:text-[14px]" />
            <h1 className="font-display text-[36px] leading-[1.08] text-black sm:text-[46px] lg:text-[54px] lg:leading-[60px]" aria-label="A relatable voice who loves Jesus.">
              <span className="block">
                <Words text="A relatable voice" mode="load" delay={0.2} stagger={0.08} />
              </span>
              <span className="block">
                <Words text="who loves Jesus." mode="load" delay={0.4} stagger={0.08} />
              </span>
            </h1>
            <FadeUp mode="load" delay={0.6}>
              <p className="max-w-[520px] text-[15.5px] leading-[27px] text-charcoal sm:text-[16.2px] sm:leading-[28.8px]">
                Stephanie Roxanne (Stephanie R. Lamar) is a Colorado-based podcast host, wife, mom, and follower of Jesus. She believes some of the most
                meaningful conversations happen when people feel safe enough to share their stories. Her podcast features two series. Testimony Tuesday gives
                guests a place to share how God has worked in their lives. The second series explores faith, culture, politics, and the everyday questions
                Christians are asking. Whether she’s behind the microphone or serving in her community, Stephanie’s heart is the same: encourage people, speak
                the truth with grace, and keep pointing others back to Jesus. She believes meaningful conversations can encourage people to grow in their faith
                and engage their communities with confidence.
              </p>
            </FadeUp>
            <FadeUp mode="load" delay={0.75} className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
              <PillButton href="/episodes" dataTestId="about-hero-listen-button">
                Listen to the Podcast
              </PillButton>
              <PillButton href="/community" variant="outline" dataTestId="about-hero-join-button">
                Join the Community
              </PillButton>
            </FadeUp>
          </div>
          <FadeUp mode="load" y={30} className="group relative mx-auto aspect-[568/640] w-full max-w-[568px] overflow-hidden rounded-2xl md:mx-0">
            <div className="absolute inset-0 transition-transform duration-[1200ms] group-hover:scale-[1.04]" style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}>
              <img alt="Portrait of Stephanie Roxanne" className="h-full w-full object-cover" src="/figma/about-media.png" />
            </div>
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-linen opacity-0 mix-blend-multiply transition-opacity duration-500 group-hover:opacity-15" />
          </FadeUp>
        </div>
      </div>
    </section>

    {/* Chapters */}
    <section className="w-full bg-white py-16 sm:py-20 lg:py-[80px]">
      <div className="mx-auto flex max-w-[1208px] flex-col gap-20 px-5 sm:px-8 lg:gap-[110px] lg:px-[116px]">
        {CHAPTERS.map((c, i) => (
          <div key={c.label} className="grid grid-cols-1 items-center gap-10 md:grid-cols-2 md:gap-14">
            <div className={`flex flex-col gap-4 ${i % 2 === 1 ? "md:order-2" : ""}`}>
              <FadeUp y={14}>
                <span className="eyebrow block text-[12px] leading-[20px] text-charcoal-mid">{c.label}</span>
              </FadeUp>
              <h2 className="font-display text-[28px] leading-[1.15] text-black sm:text-[34px] lg:text-[38px]">
                <Words text={c.heading} stagger={0.05} />
              </h2>
              <FadeUp y={16}>
                <p className="max-w-[520px] text-[14.8px] leading-[26px] text-charcoal sm:text-[15.2px] sm:leading-[27px]">{c.text}</p>
              </FadeUp>
            </div>
            <motion.div
              initial={{ clipPath: i % 2 === 0 ? "inset(0% 0% 0% 100%)" : "inset(0% 100% 0% 0%)" }}
              whileInView={{ clipPath: "inset(0% 0% 0% 0%)" }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 1, ease: EASE }}
              className="group relative aspect-[520/390] w-full overflow-hidden rounded-2xl"
            >
              <div className="absolute inset-0 transition-transform duration-[1100ms] group-hover:scale-[1.05]" style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}>
                <img alt={c.alt} className="h-full w-full object-cover" src={c.image} />
              </div>
              <div className="pointer-events-none absolute inset-0 bg-linen opacity-10 mix-blend-multiply transition-opacity duration-500 group-hover:opacity-0" />
            </motion.div>
          </div>
        ))}
      </div>
    </section>

    {/* Values & Mission */}
    <section className="w-full bg-white-smoke py-16 sm:py-20 lg:py-[88px]">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
        <div className="mx-auto flex max-w-[1208px] flex-col gap-12 lg:px-[60px]">
          <div>
            <FadeUp y={14}>
              <span className="eyebrow block text-[13px] leading-[22.4px] text-black sm:text-[14px]">Values &amp; Mission</span>
            </FadeUp>
            <h2 className="mt-3 font-display text-[32px] leading-[1.1] text-black sm:text-[38px] lg:text-[44px] lg:leading-[49.28px]">
              <Words text="What guides the table" />
            </h2>
            <FadeUp y={18}>
              <p className="mt-4 max-w-[560px] text-[14.6px] leading-[26px] text-charcoal sm:text-[15.3px] sm:leading-[27.2px]">
                Four convictions shape every conversation — the same four that shape how Stephanie lives when the microphone is off.
              </p>
            </FadeUp>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <FadeUp key={v.title} delay={0.07 * i} y={20} className="h-full">
                <div data-testid={`about-value-card-${v.title.toLowerCase()}`} className="group flex h-full flex-col gap-3 rounded-2xl border border-hairline bg-white p-7 transition-all duration-500 hover:-translate-y-1 hover:border-silver hover:shadow-[0_24px_60px_-32px_rgba(78,81,85,0.35)]">
                  <span className="font-display text-[12px] uppercase tracking-[0.22em] text-charcoal-mid">0{i + 1}</span>
                  <h3 className="font-display text-[24px] leading-[1.2] text-black">{v.title}</h3>
                  <span className="font-serif-accent text-[16px] italic leading-[24px] text-charcoal-dark">{v.sub}</span>
                  <p className="text-[13.8px] leading-[24px] text-charcoal">{v.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp y={20}>
            <blockquote className="mx-auto max-w-[760px] text-center">
              <p className="font-serif-accent text-[22px] italic leading-[33px] text-black sm:text-[26px] sm:leading-[38px]">
                “I’m not here to win arguments. I’m here to love people enough to tell the truth — and to make room for the people already living it.”
              </p>
              <cite className="mt-4 block not-italic text-[13px] uppercase tracking-[0.22em] text-charcoal-mid font-display">— Stephanie</cite>
            </blockquote>
          </FadeUp>
          <PopIn className="mx-auto">
            <PillButton href="/episodes" dataTestId="about-values-latest-button">
              Start with the Latest Episode
            </PillButton>
          </PopIn>
        </div>
      </div>
    </section>
  </main>
);

export default About;
